var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Copy } from "iconsax-react";
import { Icon } from "@iconify/react";
import { FInput } from "@ui/components/inputs/FInput";
import { FPhoneInput } from "@ui/components/inputs/FPhoneInput";
import { FDatePicker } from "@ui/components/inputs/FDatePicker";
import { SAutoComplete } from "@ui/components/inputs/SAutoComplete";
import { SectionWithInfo } from "@ui/components/section/SectionWithInfo";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getActiveUsers } from "@reducers/users/thunk/user-thunk";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { userService } from "@utility/user-service";
import { tooltipTextsApi, TOOLTIP_KEYS } from "@api/enpoints/tooltip-texts-api";
import { ClientCategoriesSection } from "./ClientCategoriesSection";
const GeneralSettings = ({ control, errors, validationRules, isEdit, ap, }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["common"]);
    const { activeUsers, activeUsersLoading } = useAppSelector((state) => state.user);
    const isSuperAdmin = userService.hasRole("Super Admin");
    const [eventLoggingTooltip, setEventLoggingTooltip] = useState("");
    // Fetch active users on component mount
    useEffect(() => {
        dispatch(getActiveUsers());
    }, [dispatch]);
    useEffect(() => {
        const fetchTooltip = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield tooltipTextsApi.getTooltipTexts();
                if (((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) && ((_b = res.data) === null || _b === void 0 ? void 0 : _b.data)) {
                    const d = res.data.data;
                    if (d[TOOLTIP_KEYS.eventLogging] != null)
                        setEventLoggingTooltip(d[TOOLTIP_KEYS.eventLogging]);
                }
            }
            catch (_c) {
                // Non-blocking
            }
        });
        fetchTooltip();
    }, []);
    const handleTooltipSave = (key, value) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            const res = yield tooltipTextsApi.saveTooltipText({ key, text: value });
            if ((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) {
                toast.success(t("common:toasts.saved"));
            }
            else {
                toast.error(t("common:toasts.failedToSaveSettings"));
            }
        }
        catch (err) {
            toast.error(t("common:toasts.failedToSaveSettings"));
        }
    });
    // Convert active users to dropdown options
    const administratorOptions = useMemo(() => {
        if (!activeUsers)
            return [];
        return activeUsers.map((user) => ({
            label: user.name,
            value: user.id,
        }));
    }, [activeUsers]);
    const handleCopyAPId = () => {
        const apId = (ap === null || ap === void 0 ? void 0 : ap.accessPointUID) || "";
        if (apId) {
            navigator.clipboard
                .writeText(apId)
                .then(() => {
                toast.success(t("common:toasts.apIdCopied"));
            })
                .catch(() => {
                toast.error(t("common:toasts.failedToCopyApId"));
            });
        }
    };
    return (React.createElement(Box, null,
        React.createElement(Typography, { variant: "h4", sx: { color: "primary.darker" } }, t("common:headings.identity")),
        React.createElement(Grid, { container: true, spacing: 1.5, sx: { mb: 2 } },
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "accessPointUID", label: t("common:labels.uniqueId"), endIcon: React.createElement(Copy, null), onEndIconClick: handleCopyAPId, disabled: isEdit, rules: validationRules.accessPointUID, error: errors.accessPointUID })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "accessPointName", label: t("common:labels.locationName"), rules: validationRules.accessPointName, error: errors.accessPointName })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "lastUpdated", label: t("common:labels.lastUpdated"), disabled: true }))),
        React.createElement(Typography, { variant: "h4", sx: { color: "primary.darker" } }, t("common:headings.general")),
        React.createElement(Grid, { container: true, spacing: 1.5, sx: { mb: 2 } },
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FDatePicker, { control: control, name: "calibrationDate", label: t("common:labels.calibrationDate"), rules: validationRules.calibrationDate, error: errors.calibrationDate, disabled: !isSuperAdmin })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FDatePicker, { control: control, name: "installationDate", label: t("common:labels.installationDate"), rules: validationRules.installationDate, error: errors.installationDate, disabled: !isSuperAdmin }))),
        React.createElement(ClientCategoriesSection, { control: control, ap: ap }),
        React.createElement(Typography, { variant: "h4", sx: { color: "primary.darker" } }, t("common:headings.administratorSettings")),
        React.createElement(Grid, { container: true, spacing: 1.5, sx: { mb: 1.5 } },
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "administratorName", label: t("common:labels.administratorName"), rules: validationRules.administratorName, error: errors.administratorName })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FPhoneInput, { control: control, name: "administratorPhone", label: t("common:labels.administratorPhone"), rules: validationRules.administratorPhone, error: errors.administratorPhone }))),
        React.createElement(Grid, { container: true, spacing: 1.5 },
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "address", label: t("common:labels.address"), rules: validationRules.address, error: errors.address })),
            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(FInput, { control: control, name: "place", label: t("common:labels.place"), rules: validationRules.place, error: errors.place })),
            isSuperAdmin && (React.createElement(Grid, { size: { xs: 12, md: 4 } },
                React.createElement(SAutoComplete, { control: control, name: "administrators", limitTags: 2, label: t("common:labels.administrator"), options: administratorOptions, loading: activeUsersLoading }))),
            React.createElement(Grid, { size: { xs: 12, md: 12 } },
                React.createElement(FInput, { control: control, name: "toolTipText", label: t("common:labels.apNote"), multiline: true, rows: 4, rules: validationRules.toolTipText, error: errors.toolTipText }))),
        isSuperAdmin && (React.createElement(React.Fragment, null,
            React.createElement(Grid, { container: true, spacing: 1.5, sx: { mt: 2 } },
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(SectionWithInfo, { title: t("common:headings.eventLogging"), tooltipText: eventLoggingTooltip, onTooltipSave: (value) => {
                            setEventLoggingTooltip(value);
                            handleTooltipSave(TOOLTIP_KEYS.eventLogging, value);
                        }, titleProps: {
                            variant: "h4",
                            sx: { color: "primary.darker" },
                            fontWeight: undefined,
                        } },
                        React.createElement(Grid, { container: true, spacing: 2 },
                            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                                React.createElement(FInput, { name: "maxLogFile", control: control, label: t("common:labels.logMaxEvents"), placeholder: "100" })),
                            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                                React.createElement(FInput, { name: "maxLogTime", control: control, label: t("common:labels.logMaxSec"), placeholder: "300", endIcon: React.createElement(Icon, { icon: "mdi-light:clock" }) })),
                            React.createElement(Grid, { size: { xs: 12, md: 4 } },
                                React.createElement(FInput, { name: "heartbeatSeconds", control: control, label: t("common:labels.heartBeatSec"), placeholder: "3600", endIcon: React.createElement(Icon, { icon: "mdi-light:clock" }) }))))))))));
};
export default GeneralSettings;
